import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../i18n/useTranslation';
import type { RecurrenceRule, Task } from '../../types/task';

/**
 * Tasks are planned in the reef's local time: due dates / times are Europe/Berlin
 * wall-clock values (the backend's APP_TIMEZONE); instants are shown in that zone.
 */
export const TASK_TIME_ZONE = 'Europe/Berlin';

// ─── Calendar-date helpers (YYYY-MM-DD, UTC arithmetic → no DST drift) ───────

function toUtcMs(date: string): number {
  return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
}

function fromUtcMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(date: string, days: number): string {
  return fromUtcMs(toUtcMs(date) + days * 86_400_000);
}

export function addMonths(date: string, months: number): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)) - 1 + months;
  const year = y + Math.floor(m / 12);
  const month = ((m % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return fromUtcMs(Date.UTC(year, month, Math.min(Number(date.slice(8, 10)), last)));
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/** 0 = Sunday … 6 = Saturday */
export function weekdayOf(date: string): number {
  return new Date(toUtcMs(date)).getUTCDay();
}

export function startOfWeek(date: string, firstDay: number): string {
  return addDays(date, -((weekdayOf(date) - firstDay + 7) % 7));
}

/** Today's date in the task time zone. */
export function todayIso(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TASK_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

const SUNDAY_FIRST_LANGUAGES = new Set(['en', 'ja']);

interface WeekInfoLocale {
  getWeekInfo?: () => { firstDay?: number };
  weekInfo?: { firstDay?: number };
}

/** First day of the week for a locale (0 = Sunday, 1 = Monday): Monday for German. */
export function firstDayOfWeek(locale: string): number {
  try {
    const intlLocale = new Intl.Locale(locale) as unknown as WeekInfoLocale;
    const info = intlLocale.getWeekInfo?.() ?? intlLocale.weekInfo;
    if (info?.firstDay) return info.firstDay % 7;
  } catch {
    // Older engines: fall back to the table below.
  }
  return SUNDAY_FIRST_LANGUAGES.has(locale.split('-')[0] ?? '') ? 0 : 1;
}

/** The rule's end date, whichever casing the API/form used. */
export function ruleEndDate(rule: RecurrenceRule | null | undefined): string | null {
  return rule?.endDate ?? rule?.end_date ?? null;
}

// ─── Locale-aware labels ──────────────────────────────────────────────────────

export interface TaskFormat {
  locale: string;
  firstDayOfWeek: number;
  /** "Sa., 10.10.2026" */
  date: (isoDate: string) => string;
  /** "Samstag, 10. Oktober 2026" */
  longDate: (isoDate: string) => string;
  /** "12:30" (de) / "12:30 PM" (en) */
  time: (hhmm: string) => string;
  /** Due date and time of a task, or null without a due date. */
  due: (task: Pick<Task, 'dueDate' | 'dueTime'>) => string | null;
  /** A UTC instant (created/updated/completed) in the task time zone. */
  dateTime: (instant: string | null | undefined) => string;
  /** "Oktober 2026" */
  monthYear: (isoDate: string) => string;
  /** Weekday names starting with the locale's first day. */
  weekdays: (style: 'short' | 'long') => Array<{ day: number; label: string }>;
  weekdayName: (day: number, style: 'short' | 'long') => string;
  category: (category: string | null | undefined) => string;
  priority: (priority: string) => string;
  status: (status: string) => string;
  recurrence: (rule: RecurrenceRule) => string;
  /** "10 ml" / "2,5 ml" — the dose of a dosing task, or null without one. */
  dose: (task: Pick<Task, 'doseAmount' | 'doseUnit'>) => string | null;
}

// A Sunday — getUTCDay() === 0 — to derive weekday names via Intl.
const SUNDAY = Date.UTC(2026, 0, 4);

export function createTaskFormat(t: TFunction, locale: string): TaskFormat {
  const utc = (opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
    new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' });
  const dateFmt = utc({ weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  const longFmt = utc({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeFmt = utc({ hour: '2-digit', minute: '2-digit' });
  const monthFmt = utc({ month: 'long', year: 'numeric' });
  const instantFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: TASK_TIME_ZONE });
  const weekdayFmt = {
    short: utc({ weekday: 'short' }),
    long: utc({ weekday: 'long' }),
  };
  const first = firstDayOfWeek(locale);
  const numberFmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 4 });

  const date = (isoDate: string): string => dateFmt.format(toUtcMs(isoDate));
  const time = (hhmm: string): string => {
    const [h = 0, m = 0] = hhmm.split(':').map(Number);
    return timeFmt.format(Date.UTC(2026, 0, 1, h, m));
  };
  const weekdayName = (day: number, style: 'short' | 'long'): string =>
    weekdayFmt[style].format(SUNDAY + day * 86_400_000);

  const recurrenceBase = (rule: RecurrenceRule): string => {
    switch (rule.frequency) {
      case 'daily': return t('tasks.recurrence.daily');
      case 'weekly': return t('tasks.recurrence.weekly');
      case 'monthly': return t('tasks.recurrence.monthly');
      case 'every_x_days':
      case 'custom':
        return t('tasks.recurrence.everyXDays', { count: rule.interval ?? rule.intervalDays ?? 1 });
      case 'specific_weekdays': {
        const days = [...(rule.weekdays ?? [])]
          .sort((a, b) => ((a - first + 7) % 7) - ((b - first + 7) % 7))
          .map((d) => weekdayName(d, 'long'));
        return t('tasks.recurrence.specificWeekdays', { days: new Intl.ListFormat(locale, { type: 'conjunction' }).format(days) });
      }
      default:
        return t('tasks.recurrence.badge');
    }
  };

  return {
    locale,
    firstDayOfWeek: first,
    date,
    longDate: (isoDate) => longFmt.format(toUtcMs(isoDate)),
    time,
    due: (task) => {
      if (!task.dueDate) return null;
      const d = date(task.dueDate.slice(0, 10));
      return task.dueTime ? `${d}, ${time(task.dueTime)}` : d;
    },
    dateTime: (instant) => {
      if (!instant) return '—';
      const d = new Date(instant);
      return Number.isNaN(d.getTime()) ? '—' : instantFmt.format(d);
    },
    monthYear: (isoDate) => monthFmt.format(toUtcMs(isoDate)),
    weekdays: (style) => Array.from({ length: 7 }, (_, i) => {
      const day = (first + i) % 7;
      return { day, label: weekdayName(day, style) };
    }),
    weekdayName,
    category: (category) => (category ? String(t(`tasks.form.categories.${category}`, { defaultValue: category })) : '—'),
    priority: (priority) => String(t(`tasks.form.priorities.${priority}`, { defaultValue: priority })),
    status: (status) => String(t(`tasks.form.statuses.${status}`, { defaultValue: status })),
    recurrence: (rule) => {
      const base = recurrenceBase(rule);
      const end = ruleEndDate(rule);
      return end ? t('tasks.recurrence.until', { rule: base, date: date(end) }) : base;
    },
    dose: (task) => {
      if (task.doseAmount === null || task.doseAmount === undefined) return null;
      const amount = numberFmt.format(task.doseAmount);
      return task.doseUnit ? `${amount} ${task.doseUnit}` : amount;
    },
  };
}

/** Locale-aware task formatting for the current app language. */
export function useTaskFormat(): TaskFormat {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  return useMemo(() => createTaskFormat(t, locale), [t, locale]);
}
